import axios from "axios";
import { getAPI, postAPI, putAPI } from "../../../api/Networking";
import { PaginatedAPIResponse } from "../../../models/NetworkResponse";
import {
  AlertDetail,
  AlertErrorCode,
  AlertItem,
  AlertListParams,
  AlertMeta,
  AlertSummary,
  normalizeAlertSummary,
} from "../../../models/Alerts/Alert";
import { CustomerAlertSettings } from "../../../models/Alerts/CustomerAlertSettings";

const ALERTS_ADMIN_BASE = "/v1/alerts/admin";

/** Carries the machine `result.code` the client branches on (e.g. ALREADY_RESOLVED). */
export class AlertsApiError extends Error {
  status: number | null;
  code: AlertErrorCode | null;
  details: Record<string, unknown> | null;

  constructor(
    message: string,
    status: number | null,
    code: AlertErrorCode | null,
    details: Record<string, unknown> | null,
  ) {
    super(message);
    this.name = "AlertsApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

const toAlertsApiError = (error: unknown, fallback: string): AlertsApiError => {
  if (error instanceof AlertsApiError) return error;
  if (axios.isAxiosError(error)) {
    const data = (error.response?.data ?? {}) as {
      message?: string;
      result?: { code?: AlertErrorCode; details?: Record<string, unknown> };
    };
    return new AlertsApiError(
      data.message || error.message || fallback,
      error.response?.status ?? null,
      data.result?.code ?? null,
      data.result?.details ?? null,
    );
  }
  const message = error instanceof Error ? error.message : fallback;
  return new AlertsApiError(message || fallback, null, null, null);
};

const unwrap = <T>(
  response: { success?: boolean; message?: string; result?: unknown },
  fallback: string,
): T => {
  if (!response || response.success === false) {
    const result = (response?.result ?? {}) as {
      code?: AlertErrorCode;
      details?: Record<string, unknown>;
    };
    throw new AlertsApiError(
      response?.message || fallback,
      null,
      result.code ?? null,
      result.details ?? null,
    );
  }
  return response.result as T;
};

const buildQuery = (params: AlertListParams): string => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      query.append(key, String(value));
    }
  });
  const text = query.toString();
  return text ? `?${text}` : "";
};

export default class AlertsAPI {
  getSummary = async (): Promise<AlertSummary> => {
    try {
      const response = await getAPI(`${ALERTS_ADMIN_BASE}/summary`);
      const result = unwrap<AlertSummary>(response, "Failed to load alerts.");
      return normalizeAlertSummary({
        cursor: result?.cursor ?? null,
        total_unread: result?.total_unread ?? 0,
        total_unread_grouped: result?.total_unread_grouped,
        total_high_unread: result?.total_high_unread ?? 0,
        sections: Array.isArray(result?.sections) ? result.sections : [],
      });
    } catch (error) {
      throw toAlertsApiError(error, "Failed to load alerts.");
    }
  };

  getMeta = async (): Promise<AlertMeta> => {
    try {
      const response = await getAPI(`${ALERTS_ADMIN_BASE}/meta`);
      const result = unwrap<AlertMeta>(response, "Failed to load alert setup.");
      return {
        sections: result?.sections ?? [],
        severities: result?.severities ?? [],
        ref_types: result?.ref_types ?? [],
        alert_types: result?.alert_types ?? [],
      };
    } catch (error) {
      throw toAlertsApiError(error, "Failed to load alert setup.");
    }
  };

  listAlerts = async (
    params: AlertListParams,
  ): Promise<PaginatedAPIResponse<AlertItem>> => {
    try {
      const response = await getAPI(
        `${ALERTS_ADMIN_BASE}${buildQuery(params)}`,
      );
      const result = unwrap<PaginatedAPIResponse<AlertItem>>(
        response,
        "Failed to load alerts.",
      );
      return {
        page_no: result?.page_no ?? 1,
        total_pages: result?.total_pages ?? 0,
        total_count: result?.total_count ?? 0,
        limit: result?.limit ?? params.limit ?? 30,
        list: Array.isArray(result?.list) ? result.list : [],
      };
    } catch (error) {
      throw toAlertsApiError(error, "Failed to load alerts.");
    }
  };

  getAlert = async (alertId: number): Promise<AlertDetail> => {
    try {
      const response = await getAPI(`${ALERTS_ADMIN_BASE}/${alertId}`);
      const result = unwrap<AlertDetail>(response, "Failed to load alert.");
      return {
        ...result,
        payload: result?.payload ?? {},
        recipients: result?.recipients ?? [],
        activity: result?.activity ?? [],
      };
    } catch (error) {
      throw toAlertsApiError(error, "Failed to load alert.");
    }
  };

  markRead = async (alertIds: number[]): Promise<number> => {
    try {
      const response = await postAPI(`${ALERTS_ADMIN_BASE}/read`, {
        alert_ids: alertIds,
      });
      return unwrap<{ updated: number }>(response, "Failed to mark read.")
        ?.updated;
    } catch (error) {
      throw toAlertsApiError(error, "Failed to mark read.");
    }
  };

  markAllRead = async (section?: string): Promise<number> => {
    try {
      const response = await postAPI(
        `${ALERTS_ADMIN_BASE}/read-all`,
        section ? { section } : {},
      );
      return unwrap<{ updated: number }>(response, "Failed to mark all read.")
        ?.updated;
    } catch (error) {
      throw toAlertsApiError(error, "Failed to mark all read.");
    }
  };

  archive = async (alertIds: number[]): Promise<number> => {
    try {
      const response = await postAPI(`${ALERTS_ADMIN_BASE}/archive`, {
        alert_ids: alertIds,
      });
      return unwrap<{ updated: number }>(response, "Failed to archive.")
        ?.updated;
    } catch (error) {
      throw toAlertsApiError(error, "Failed to archive.");
    }
  };

  unarchive = async (alertIds: number[]): Promise<number> => {
    try {
      const response = await postAPI(`${ALERTS_ADMIN_BASE}/unarchive`, {
        alert_ids: alertIds,
      });
      return unwrap<{ updated: number }>(response, "Failed to unarchive.")
        ?.updated;
    } catch (error) {
      throw toAlertsApiError(error, "Failed to unarchive.");
    }
  };

  resolve = async (alertId: number, note: string): Promise<AlertItem> => {
    try {
      const response = await postAPI(
        `${ALERTS_ADMIN_BASE}/${alertId}/resolve`,
        { note },
      );
      return unwrap<AlertItem>(response, "Failed to resolve alert.");
    } catch (error) {
      throw toAlertsApiError(error, "Failed to resolve alert.");
    }
  };

  /** Client alert master switch and per-type on/off state for one customer. */
  getCustomerSettings = async (
    customerId: number,
  ): Promise<CustomerAlertSettings> => {
    try {
      const response = await getAPI(
        `${ALERTS_ADMIN_BASE}/customers/${customerId}/settings`,
      );
      return unwrap<CustomerAlertSettings>(
        response,
        "Failed to load client alert settings.",
      );
    } catch (error) {
      throw toAlertsApiError(error, "Failed to load client alert settings.");
    }
  };

  /** Switches one client alert type for a customer; saves immediately and is activity-logged. */
  setCustomerAlertType = async (
    customerId: number,
    code: string,
    isEnabled: boolean,
  ): Promise<CustomerAlertSettings> => {
    try {
      const response = await putAPI(
        `${ALERTS_ADMIN_BASE}/customers/${customerId}/settings/types/${encodeURIComponent(code)}`,
        { is_enabled: isEnabled },
      );
      return unwrap<CustomerAlertSettings>(
        response,
        "Failed to save client alert setting.",
      );
    } catch (error) {
      throw toAlertsApiError(error, "Failed to save client alert setting.");
    }
  };
}
